import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  useMediaQuery,
  useTheme,
  TextField
} from '@mui/material';
import { Logout as LogoutIcon, Description as DescriptionIcon } from '@mui/icons-material';

const NICKNAME_MIN = 2;
const NICKNAME_MAX = 64;

const FALLBACK = {
  'formalDocuments.title': 'Accept Formal Documents',
  'formalDocuments.description': 'Please read and accept the following documents to continue to the portal.',
  'formalDocuments.termsLabel': 'Terms and Conditions',
  'formalDocuments.privacyLabel': 'Privacy Policy',
  'formalDocuments.openLink': '(Open link)',
  'formalDocuments.logout': 'Logout',
  'formalDocuments.confirm': 'Confirm',
  'formalDocuments.confirming': 'Confirming...',
  'formalDocuments.tournamentNicknameLabel': 'Tournament nickname',
  'formalDocuments.tournamentNicknamePlaceholder': 'Name shown on contest leaderboards',
  'formalDocuments.tournamentNicknameHelper': 'This applies to your dealer organization for tournament contests.'
};

const FormalDocumentsAcceptanceDialog = ({
  open,
  terms,
  privacy,
  extra,
  requireTournamentNickname,
  onConfirm,
  onLogout,
  t
}) => {
  const getT = (key) => (t && typeof t === 'function' ? t(key) : (FALLBACK[key] || key));
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [extraChecked, setExtraChecked] = useState(false);
  const [tournamentNickname, setTournamentNickname] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTermsChecked(false);
      setPrivacyChecked(false);
      setExtraChecked(false);
      setTournamentNickname('');
    }
  }, [open]);

  const linkRows = [
    terms?.url
      ? {
          key: 'terms',
          label: getT('formalDocuments.termsLabel'),
          url: terms.url,
          checked: termsChecked,
          setChecked: setTermsChecked
        }
      : null,
    privacy?.url
      ? {
          key: 'privacy',
          label: getT('formalDocuments.privacyLabel'),
          url: privacy.url,
          checked: privacyChecked,
          setChecked: setPrivacyChecked
        }
      : null
  ].filter(Boolean);

  const extraText = (extra?.text ?? '').trim();
  const showExtra = Boolean(extraText);

  const allChecked =
    linkRows.every((d) => d.checked) &&
    (!showExtra || extraChecked);

  const trimmedNick = tournamentNickname.trim();
  const nicknameOk =
    !requireTournamentNickname ||
    (trimmedNick.length >= NICKNAME_MIN && trimmedNick.length <= NICKNAME_MAX);

  const canConfirm =
    allChecked &&
    nicknameOk &&
    !loading &&
    (linkRows.length > 0 || showExtra);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    try {
      await onConfirm({
        tournamentNickname: requireTournamentNickname ? trimmedNick : undefined
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (!loading) {
      onLogout();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      disableEscapeKeyDown
      onClose={() => {}}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon color="primary" />
          <Typography variant="h6">{getT('formalDocuments.title')}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {getT('formalDocuments.description')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {linkRows.map((doc) => (
            <FormControlLabel
              key={doc.key}
              control={
                <Checkbox
                  checked={doc.checked}
                  onChange={(e) => doc.setChecked(e.target.checked)}
                  disabled={loading}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2">{doc.label}</Typography>
                  <Button
                    component="a"
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                  >
                    {getT('formalDocuments.openLink')}
                  </Button>
                </Box>
              }
            />
          ))}
          {showExtra && (
            <FormControlLabel
              key="extra-confirmation"
              control={
                <Checkbox
                  checked={extraChecked}
                  onChange={(e) => setExtraChecked(e.target.checked)}
                  disabled={loading}
                />
              }
              label={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {extraText}
                </Typography>
              }
            />
          )}
          {requireTournamentNickname && (
            <TextField
              label={getT('formalDocuments.tournamentNicknameLabel')}
              placeholder={getT('formalDocuments.tournamentNicknamePlaceholder')}
              helperText={getT('formalDocuments.tournamentNicknameHelper')}
              value={tournamentNickname}
              onChange={(e) => setTournamentNickname(e.target.value)}
              disabled={loading}
              fullWidth
              inputProps={{ maxLength: NICKNAME_MAX }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleLogout}
          disabled={loading}
          color="inherit"
          startIcon={<LogoutIcon />}
        >
          {getT('formalDocuments.logout')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? getT('formalDocuments.confirming') : getT('formalDocuments.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormalDocumentsAcceptanceDialog;
